import React, { CSSProperties } from "react";
import { sliderStyles } from "../../../config/styleConfig";
import { HeroSlide } from "./types";

interface HeroSliderExportProps {
  slides?: HeroSlide[];
  minHeight?: number;
  showDots?: boolean;
  showArrows?: boolean;
  titleColor?: string;
  subtitleColor?: string;
}

export const HeroSliderExport: React.FC<HeroSliderExportProps> = ({
  slides = [],
  minHeight = 500,
  showDots = true,
  showArrows = true,
  titleColor = "#ffffff",
  subtitleColor = "#e5e7eb",
}) => {
  const safeSlides = Array.isArray(slides) ? slides : [];
  // Use sliderStyles for HeroSlider-specific typography
  const titleFontSize = sliderStyles.title.desktop.fontSize;
  const subtFontSize = sliderStyles.subtitle.desktop.fontSize;
  const arrowStyles = sliderStyles.arrow.desktop;
  const dotStyles = sliderStyles.dot.desktop;
  const buttonStyles = sliderStyles.button.desktop;

  if (safeSlides.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: minHeight,
          backgroundColor: "#1e3a5f",
          color: "#ffffff",
          width: "100%",
        }}
      >
        <p style={{ textAlign: "center", opacity: 0.8 }}>No slides configured</p>
      </div>
    );
  }

  const getSlideStyle = (slide: HeroSlide, index: number): CSSProperties => ({
    position: index === 0 ? "relative" : "absolute",
    top: 0,
    left: 0,
    width: "100%",
    minHeight: minHeight,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: slide.backgroundColor || "#1e3a5f",
    opacity: index === 0 ? 1 : 0,
    zIndex: index === 0 ? 10 : 0,
    transition: "opacity 0.5s ease-in-out",
    overflow: "hidden",
  });

  return (
    <div className="hero-slider" style={{ position: "relative", overflow: "hidden", width: "100%" }}>
      {/* Render ALL slides */}
      {safeSlides.map((slide, index) => (
        <div key={index} data-slide={index} style={getSlideStyle(slide, index)}>
          {slide.backgroundImage && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${slide.backgroundImage})`,
                backgroundSize: "cover",
                backgroundPosition: slide.backgroundPosition || "50% 50%",
                backgroundRepeat: "no-repeat",
                transform: `scale(${slide.backgroundZoom || 1})`,
                transformOrigin: slide.backgroundPosition || "50% 50%",
              }}
            />
          )}
          {slide.backgroundImage && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.4)",
              }}
            />
          )}
          <div
            style={{
              position: "relative",
              zIndex: 1,
              textAlign: "center",
              maxWidth: "56rem",
              padding: "32px 24px",
            }}
          >
            <h1
              style={{
                fontSize: titleFontSize,
                fontWeight: 700,
                color: titleColor,
                marginBottom: "16px",
                marginTop: 0,
              }}
            >
              {slide.title}
            </h1>
            {slide.subtitle && (
              <p
                style={{
                  fontSize: subtFontSize,
                  color: subtitleColor,
                  marginBottom: "32px",
                  marginTop: 0,
                }}
              >
                {slide.subtitle}
              </p>
            )}
            {slide.buttonText && (
              <a
                href={slide.buttonLink || "#"}
                style={{
                  display: "inline-block",
                  padding: `${buttonStyles.py}px ${buttonStyles.px}px`,
                  backgroundColor: "var(--color-primary, #3b82f6)",
                  color: "#ffffff",
                  borderRadius: "8px",
                  fontWeight: 500,
                  fontSize: buttonStyles.fontSize,
                  textDecoration: "none",
                  transition: "all 0.2s",
                }}
              >
                {slide.buttonText}
              </a>
            )}
          </div>
        </div>
      ))}

      {/* Dots indicator - active dot has pill-shape (wider), inactive is circular */}
      {showDots && safeSlides.length > 1 && (
        <div
          style={{
            position: "absolute",
            bottom: `${dotStyles.bottom}px`,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            gap: `${dotStyles.gap}px`,
            zIndex: 20,
          }}
        >
          {safeSlides.map((_, index) => (
            <button
              key={index}
              className="slider-dot"
              data-index={index}
              style={{
                width: index === 0 ? `${dotStyles.activeWidth}px` : `${dotStyles.inactiveWidth}px`,
                height: `${dotStyles.height}px`,
                borderRadius: `${dotStyles.borderRadius}px`,
                border: "none",
                backgroundColor: index === 0 ? sliderStyles.dotBackground.active : sliderStyles.dotBackground.inactive,
                cursor: "pointer",
                padding: 0,
                transition: "all 0.2s",
              }}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Navigation arrows - semi-transparent white background with blur */}
      {showArrows && safeSlides.length > 1 && (
        <>
          <button
            className="slider-prev"
            style={{
              position: "absolute",
              left: `${arrowStyles.position}px`,
              top: "50%",
              transform: "translateY(-50%)",
              width: `${arrowStyles.size}px`,
              height: `${arrowStyles.size}px`,
              minWidth: `${arrowStyles.size}px`,
              minHeight: `${arrowStyles.size}px`,
              aspectRatio: "1 / 1",
              borderRadius: "50%",
              backgroundColor: sliderStyles.arrowBackground.default,
              backdropFilter: "blur(4px)",
              border: "none",
              padding: 0,
              margin: 0,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 20,
              transition: "all 0.2s",
              boxSizing: "border-box",
            }}
            aria-label="Previous slide"
          >
            <svg
              width={arrowStyles.iconSize}
              height={arrowStyles.iconSize}
              fill="none"
              stroke="#ffffff"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            className="slider-next"
            style={{
              position: "absolute",
              right: `${arrowStyles.position}px`,
              top: "50%",
              transform: "translateY(-50%)",
              width: `${arrowStyles.size}px`,
              height: `${arrowStyles.size}px`,
              minWidth: `${arrowStyles.size}px`,
              minHeight: `${arrowStyles.size}px`,
              aspectRatio: "1 / 1",
              borderRadius: "50%",
              backgroundColor: sliderStyles.arrowBackground.default,
              backdropFilter: "blur(4px)",
              border: "none",
              padding: 0,
              margin: 0,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 20,
              transition: "all 0.2s",
              boxSizing: "border-box",
            }}
            aria-label="Next slide"
          >
            <svg
              width={arrowStyles.iconSize}
              height={arrowStyles.iconSize}
              fill="none"
              stroke="#ffffff"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}
    </div>
  );
};
